/**
 * Declarative spec for the 50-page global utility tool cluster.
 * SSR renders the skeleton (headings, static tables, copy, FAQs);
 * the client island only re-runs `compute` on input change.
 */
export type ToolCluster =
  | "fitness"
  | "finance"
  | "datetime"
  | "digital"
  | "lifestyle"
  | "build"
  | "auto"
  | "power"
  | "craft"
  | "homestead"
  | "tech"
  | "business";

export type FieldType = "number" | "select" | "time" | "date" | "text";

export interface ToolField {
  id: string;
  label: string;
  type: FieldType;
  def: string;
  options?: { value: string; label: string }[];
  unit?: string;
  min?: number;
  max?: number;
  step?: number;
  help?: string;
}

export interface ToolCard {
  label: string;
  value: string;
  sub?: string;
  accent?: boolean;
}

export interface ToolTable {
  title: string;
  head: string[];
  rows: (string | number)[][];
  caption?: string;
}

export interface ToolBar {
  label: string;
  value: number;
  display: string;
}

export interface ToolOutput {
  cards: ToolCard[];
  summary?: string;
  table?: ToolTable;
  bars?: ToolBar[];
}

export type ToolValues = Record<string, string>;

export interface ToolSpec {
  slug: string;
  cluster: ToolCluster;
  name: string;
  h1: string;
  title: string;
  description: string;
  /** Long-tail micro-intent queries embedded in SSR copy. */
  keywords: string[];
  inputs: ToolField[];
  compute: (v: ToolValues) => ToolOutput;
  intro: string;
  sections: { h: string; body: string }[];
  staticTable: ToolTable;
  faqs: { q: string; a: string }[];
  related?: string[];
  /** Optional formula / step-by-step maths box (additive: older tools omit it). */
  formula?: {
    expressions: string[];
    variables?: { name: string; meaning: string }[];
  };
}

/* ----------------------------- helpers ----------------------------- */

export const n = (v: ToolValues, k: string, fallback = 0): number => {
  const x = Number(v[k]);
  return Number.isFinite(x) ? x : fallback;
};

export const s = (v: ToolValues, k: string, fallback = ""): string => v[k] ?? fallback;

export const fmt = (x: number, digits = 0): string =>
  Number.isFinite(x)
    ? x.toLocaleString("en-US", { minimumFractionDigits: digits, maximumFractionDigits: digits })
    : "—";

export const money = (x: number, sym = "$", digits = 0): string => `${sym}${fmt(Math.round(x * 100) / 100, digits)}`;

export const defaults = (spec: ToolSpec): ToolValues =>
  Object.fromEntries(spec.inputs.map((f) => [f.id, f.def]));